import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Loader2, Check, Download } from "lucide-react";
import Seo from "@/components/Seo";
import BookingForm from "@/components/BookingForm";
import { ASSESSMENT_FIELDS } from "@/data/content";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STEPS = ["Identity", "Industry & Scale", "Operations", "Compliance & Goals", "AI Posture", "Review"];

function CheckChip({ label, active, onToggle, testId }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testId}
      className={`px-4 py-2.5 border text-xs tracking-[0.14em] uppercase transition-all duration-300 ${
        active
          ? "bg-gold text-navy border-gold"
          : "bg-transparent text-[#3F4A6B] border-[#001B54]/12 hover:border-gold/60 hover:text-[#001B54]"
      }`}
    >
      {label}
    </button>
  );
}

function ScoreDial({ label, value, testId }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const c = 2 * Math.PI * 46;
  return (
    <div className="flex flex-col items-center" data-testid={testId}>
      <svg width="140" height="140" viewBox="0 0 120 120" className="-rotate-90">
        <circle cx="60" cy="60" r="46" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="6" />
        <circle
          cx="60" cy="60" r="46" fill="none" stroke="#D4AF37"
          strokeWidth="6" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (v / 100) * c}
        />
      </svg>
      <div className="-mt-[105px] font-display text-4xl gold-text">{v}</div>
      <div className="mt-12 text-[0.65rem] tracking-[0.28em] uppercase text-[#3F4A6B]">{label}</div>
    </div>
  );
}

export default function AIAdvisor() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    company: "",
    role: "",
    industry: "",
    company_size: "",
    current_systems: [],
    primary_challenges: [],
    compliance_requirements: [],
    growth_goals: [],
    ai_readiness: "",
    additional_context: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);

  const toggle = (key, value) => {
    setForm((f) => {
      const list = f[key];
      const has = list.includes(value);
      return { ...f, [key]: has ? list.filter((v) => v !== value) : [...list, value] };
    });
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const canNext = () => {
    if (step === 0) return form.full_name && form.email && form.company;
    if (step === 1) return form.industry && form.company_size;
    if (step === 2) return form.current_systems.length > 0 && form.primary_challenges.length > 0;
    if (step === 3) return form.compliance_requirements.length > 0 && form.growth_goals.length > 0;
    if (step === 4) return !!form.ai_readiness;
    return true;
  };

  async function submit() {
    setSubmitting(true);
    try {
      const { data } = await api.post("/assessments", form);
      setReport(data.report);
      setAssessmentId(data.id);
      toast.success("Executive assessment generated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (report) {
    async function downloadPdf() {
      try {
        const res = await fetch(`${API_BASE}/assessments/pdf`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            full_name: form.full_name,
            email: form.email,
            company: form.company,
            industry: form.industry,
            company_size: form.company_size,
            ai_readiness: form.ai_readiness,
            report,
          }),
        });
        if (!res.ok) throw new Error("PDF generation failed");
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `alchamis-executive-memo-${form.company.replace(/\s+/g, "-").toLowerCase()}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Memo downloaded.");
      } catch (err) {
        toast.error("Could not generate PDF. Please retry.");
      }
    }

    return (
      <div data-testid="advisor-report" className="pt-32 pb-24">
        <Seo title="Executive Diagnostic Memo" description="Personalised AlchamisGroup diagnostic memo." noindex />
        <section className="max-w-[1200px] mx-auto px-6 md:px-12">
          <span className="overline">AlchamisGroup · Executive Diagnostic Memo</span>
          <h1 className="font-display text-4xl sm:text-6xl text-[#001B54] mt-6 leading-tight">
            Prepared for <span className="italic gold-text">{form.full_name}</span>
            <span className="block text-[#3F4A6B] text-2xl mt-2 not-italic">{form.company}</span>
          </h1>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={downloadPdf} className="luxury-button" data-testid="advisor-download-pdf">
              <Download size={14} /> Download Executive Memo (PDF)
            </button>
            <Link to="/contact" className="luxury-button-ghost" data-testid="advisor-cta-contact">
              Engage AlchamisGroup <ArrowRight size={14} />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6 bg-white border border-[#001B54]/10 shadow-[0_24px_60px_-30px_rgba(0,27,84,0.18)] p-10">
            <ScoreDial label="Business Maturity" value={report.business_maturity_score} testId="score-maturity" />
            <ScoreDial label="Digital Transformation" value={report.digital_transformation_score} testId="score-digital" />
            <ScoreDial label="AI Readiness" value={report.ai_readiness_score} testId="score-ai" />
            <ScoreDial label="Lead Priority" value={report.lead_score} testId="score-lead" />
          </div>

          <div className="mt-16 grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-7">
              <span className="overline">Executive Summary</span>
              <p className="font-display text-2xl text-[#001B54] mt-4 leading-relaxed whitespace-pre-wrap">
                {report.executive_summary}
              </p>
              <div className="hairline mt-12 mb-12 max-w-[140px]" />
              <span className="overline">Strategic Priorities</span>
              <ol className="mt-4 space-y-3 text-[#0A1228]">
                {(report.strategic_priorities || []).map((p, i) => (
                  <li key={i} className="flex gap-4 items-start">
                    <span className="number-badge mt-1">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-base leading-relaxed">{p}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="col-span-12 lg:col-span-5 space-y-10">
              <div>
                <span className="overline">Recommended Consulting</span>
                <ul className="mt-4 space-y-4">
                  {(report.recommended_consulting_services || []).map((r, i) => (
                    <li key={i} className="border border-[#001B54]/10 p-5">
                      <div className="font-display text-xl text-[#001B54]">{r.name}</div>
                      <div className="text-[#3F4A6B] text-sm mt-2 leading-relaxed">{r.rationale}</div>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <span className="overline">Recommended Platforms</span>
                <ul className="mt-4 space-y-4">
                  {(report.recommended_solutions || []).map((r, i) => (
                    <li key={i} className="border border-gold/30 p-5 bg-gold/[0.04]">
                      <div className="font-display text-xl text-gold">{r.name}</div>
                      <div className="text-[#3F4A6B] text-sm mt-2 leading-relaxed">{r.fit_reason}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <span className="overline">Recommended Next Steps</span>
              <ol className="mt-4 space-y-3 text-[#0A1228]">
                {(report.next_steps || []).map((s, i) => (
                  <li key={i} className="flex gap-3"><Check size={16} className="text-gold mt-1" /><span>{s}</span></li>
                ))}
              </ol>
            </div>
            <div>
              <span className="overline">Risk Alerts</span>
              <ul className="mt-4 space-y-3 text-[#0A1228]">
                {(report.risk_alerts || []).map((r, i) => (
                  <li key={i} className="border-l-2 border-gold pl-4 text-[#3F4A6B] leading-relaxed">{r}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-20 grid grid-cols-12 gap-8 items-start">
            <div className="col-span-12 lg:col-span-5">
              <span className="overline">Next Step</span>
              <h2 className="font-display text-4xl text-[#001B54] mt-4 leading-tight">
                Schedule a private briefing
                <span className="block italic gold-text">with a partner.</span>
              </h2>
              <p className="text-[#3F4A6B] mt-5 leading-relaxed">
                Use the calendar to choose a 30-minute slot. Your assessment context is attached automatically —
                the partner reviewing your memo will arrive prepared.
              </p>
              <div className="mt-8 space-y-3">
                <button onClick={downloadPdf} className="luxury-button" data-testid="advisor-download-pdf-bottom">
                  <Download size={14} /> Download Memo (PDF)
                </button>
                <div className="block">
                  <Link to="/contact" className="luxury-button-ghost" data-testid="advisor-cta-contact-bottom">
                    Send a Private Note <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-span-12 lg:col-span-7">
              <BookingForm
                source="ai-advisor"
                prefill={{ name: form.full_name, email: form.email, company: form.company, role: form.role }}
                assessmentId={assessmentId}
                testIdPrefix="advisor-booking"
              />
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="advisor-form-page" className="pt-32 pb-24">
      <Seo
        title="AI Business Advisor"
        description="A McKinsey-grade AI diagnostic that scores maturity, transformation, and AI readiness — delivered as an executive memo."
        path="/ai-advisor"
      />
      <section className="max-w-[1200px] mx-auto px-6 md:px-12">
        <span className="overline">AI Business Advisor</span>
        <h1 className="font-display text-4xl sm:text-6xl text-[#001B54] mt-6 leading-[1.05]">
          A private diagnostic.
          <span className="block italic gold-text">Delivered like a board memo.</span>
        </h1>
        <p className="text-[#3F4A6B] mt-6 max-w-2xl leading-relaxed">
          Six minutes of executive intake. Calibrated maturity scoring. A personalised recommendation
          across our consulting practices and enterprise platforms.
        </p>

        <div className="mt-12 flex items-center gap-3 overflow-x-auto no-scrollbar">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-3">
              <div className={`flex items-center gap-2 ${i === step ? "text-gold" : i < step ? "text-[#3F4A6B]" : "text-[#001B54]/30"}`}>
                <span className="number-badge">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-xs tracking-[0.18em] uppercase">{label}</span>
              </div>
              {i < STEPS.length - 1 && <span className="h-px w-10 bg-white/15" />}
            </div>
          ))}
        </div>

        <div className="mt-12 bg-white border border-[#001B54]/10 shadow-[0_24px_60px_-30px_rgba(0,27,84,0.18)] p-8 md:p-12 min-h-[420px]" data-testid={`advisor-step-${step}`}>
          {step === 0 && (
            <div className="space-y-6">
              <div className="font-display text-3xl text-[#001B54]">Identify yourself.</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  data-testid="advisor-input-name"
                  className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]" />
                <input placeholder="Business email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="advisor-input-email"
                  className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]" />
                <input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                  data-testid="advisor-input-company"
                  className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]" />
                <input placeholder="Role / title" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="advisor-input-role"
                  className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]" />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-8">
              <div className="font-display text-3xl text-[#001B54]">Define the operating context.</div>
              <div>
                <div className="overline mb-3">Industry</div>
                <div className="flex flex-wrap gap-2">
                  {ASSESSMENT_FIELDS.industries.map((i) => (
                    <CheckChip key={i} label={i} active={form.industry === i} onToggle={() => setForm({ ...form, industry: i })} testId={`advisor-industry-${i}`} />
                  ))}
                </div>
              </div>
              <div>
                <div className="overline mb-3">Company Size</div>
                <div className="flex flex-wrap gap-2">
                  {ASSESSMENT_FIELDS.company_sizes.map((s) => (
                    <CheckChip key={s.value} label={s.label} active={form.company_size === s.value} onToggle={() => setForm({ ...form, company_size: s.value })} testId={`advisor-size-${s.value}`} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-8">
              <div className="font-display text-3xl text-[#001B54]">Audit the operating stack.</div>
              <div>
                <div className="overline mb-3">Current Systems (select all that apply)</div>
                <div className="flex flex-wrap gap-2">
                  {ASSESSMENT_FIELDS.current_systems.map((i) => (
                    <CheckChip key={i} label={i} active={form.current_systems.includes(i)} onToggle={() => toggle("current_systems", i)} testId={`advisor-sys-${i}`} />
                  ))}
                </div>
              </div>
              <div>
                <div className="overline mb-3">Primary Challenges</div>
                <div className="flex flex-wrap gap-2">
                  {ASSESSMENT_FIELDS.challenges.map((i) => (
                    <CheckChip key={i} label={i} active={form.primary_challenges.includes(i)} onToggle={() => toggle("primary_challenges", i)} testId={`advisor-challenge-${i}`} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-8">
              <div className="font-display text-3xl text-[#001B54]">Governance and ambition.</div>
              <div>
                <div className="overline mb-3">Compliance Requirements</div>
                <div className="flex flex-wrap gap-2">
                  {ASSESSMENT_FIELDS.compliance.map((i) => (
                    <CheckChip key={i} label={i} active={form.compliance_requirements.includes(i)} onToggle={() => toggle("compliance_requirements", i)} testId={`advisor-comp-${i}`} />
                  ))}
                </div>
              </div>
              <div>
                <div className="overline mb-3">Growth Goals</div>
                <div className="flex flex-wrap gap-2">
                  {ASSESSMENT_FIELDS.goals.map((i) => (
                    <CheckChip key={i} label={i} active={form.growth_goals.includes(i)} onToggle={() => toggle("growth_goals", i)} testId={`advisor-goal-${i}`} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-8">
              <div className="font-display text-3xl text-[#001B54]">AI posture.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {ASSESSMENT_FIELDS.ai_stages.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => setForm({ ...form, ai_readiness: s.value })}
                    data-testid={`advisor-ai-${s.value}`}
                    className={`p-6 border text-left transition-all duration-300 ${
                      form.ai_readiness === s.value ? "border-gold bg-gold/[0.06]" : "border-[#001B54]/12 hover:border-gold/40"
                    }`}
                  >
                    <div className="number-badge">{s.value.toUpperCase()}</div>
                    <div className="font-display text-2xl text-[#001B54] mt-3">{s.label}</div>
                  </button>
                ))}
              </div>
              <div>
                <div className="overline mb-3">Additional Context (optional)</div>
                <textarea rows={4} value={form.additional_context}
                  onChange={(e) => setForm({ ...form, additional_context: e.target.value })}
                  data-testid="advisor-additional"
                  placeholder="Any context that would sharpen our diagnosis."
                  className="w-full bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3 text-sm text-[#001B54]" />
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-8">
              <div className="font-display text-3xl text-[#001B54]">Confirm and generate.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                <div><div className="overline mb-2">Name</div><div className="text-[#001B54]">{form.full_name}</div></div>
                <div><div className="overline mb-2">Email</div><div className="text-[#001B54]">{form.email}</div></div>
                <div><div className="overline mb-2">Company</div><div className="text-[#001B54]">{form.company}</div></div>
                <div><div className="overline mb-2">Industry</div><div className="text-[#001B54]">{form.industry}</div></div>
                <div><div className="overline mb-2">Size</div><div className="text-[#001B54]">{form.company_size}</div></div>
                <div><div className="overline mb-2">AI readiness</div><div className="text-[#001B54]">{form.ai_readiness}</div></div>
                <div className="md:col-span-2"><div className="overline mb-2">Systems</div><div className="text-[#001B54]">{form.current_systems.join(", ")}</div></div>
                <div className="md:col-span-2"><div className="overline mb-2">Challenges</div><div className="text-[#001B54]">{form.primary_challenges.join(", ")}</div></div>
                <div className="md:col-span-2"><div className="overline mb-2">Compliance</div><div className="text-[#001B54]">{form.compliance_requirements.join(", ")}</div></div>
                <div className="md:col-span-2"><div className="overline mb-2">Goals</div><div className="text-[#001B54]">{form.growth_goals.join(", ")}</div></div>
              </div>
              <p className="text-[#6B7390] text-xs leading-relaxed">
                Submitting will engage our AI Business Advisor (Claude Sonnet 4.5) to produce a McKinsey-grade
                diagnostic memo. The result is stored in our private CRM and surfaced to the engagement partner.
              </p>
            </div>
          )}
        </div>

        <div className="mt-8 flex items-center justify-between">
          <button onClick={prev} disabled={step === 0} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#3F4A6B] disabled:opacity-30 hover:text-gold"
            data-testid="advisor-back">
            <ArrowLeft size={14} /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} disabled={!canNext()} className="luxury-button disabled:opacity-40" data-testid="advisor-next">
              Continue <ArrowRight size={14} />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting} className="luxury-button" data-testid="advisor-submit">
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Composing memo…</> : <>Generate Diagnostic <ArrowRight size={14} /></>}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
